import { call, put, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { locationQuerySelector, tokensSelector } from 'ctm-selectors';
import { get } from 'ctm-util-functions';

/**
 * Private method (exported for testing).
 * Determine storage medium, default to storage, fallback to redux store.
 * @param {Immutable.Map} query - Location query object.
 * @returns {Object} Interface that allows read/write to storage.
 */
export const _determineStorageMedium = (query = {}) => {
    if (get(query, 'load_journey')) {
        return sessionStorage;
    }
    return localStorage;
};

/**
 * Private method (exported for testing).
 * Return a storage interface that can read and write tokens.
 * We are using a defined interface instead of the localStorage interface,
 * so we can change the storage medium without affecting the code using this.
 *
 * @returns {Object} Interface that allows read/write to storage.
 */
export const _getStorage = function* () {
    const query = yield select(locationQuerySelector);
    const tokens = yield select(tokensSelector);

    // Getter
    const getToken = function* (name) {
        try {
            // try get from redux store first
            let token = yield call(get, tokens, name);
            if (!token) {
                // if no token in store, try get from local storage
                token = yield call([_determineStorageMedium(query), 'getItem'], name);
                // Set token into local storage so we can access them from store later
                yield put({
                    type: 'SET_AUTH_TOKENS',
                    [name]: token
                });
            }
            return token;
        } catch (error) {
            return yield call(get, tokens, name);
        }
    };

    // Setter
    const setToken = function* (name, value) {
        // Set token to the store so we can access them through tokensSelector from places other than Saga
        yield put({
            type: 'SET_AUTH_TOKENS',
            [name]: value
        });

        // Try set token in local storage, it may not be available if user's browser is in private mode
        try {
            yield call([_determineStorageMedium(query), 'setItem'], name, value);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('local/session storage is not available, browser in private mode');
        }
    };

    // Remove
    const removeToken = function* (name) {
        // Remove token from store
        yield put({
            type: 'REMOVE_AUTH_TOKEN',
            name
        });

        // Try delete token from local storage, it may not be available if user's browser is in private mode
        try {
            yield call([_determineStorageMedium(query), 'removeItem'], name);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('local/session storage is not available, browser in private mode');
        }
    };

    // Storage interface.
    return {
        getToken,
        setToken,
        removeToken
    };
};

/**
 * Private method (exported for testing).
 * Get the specified token out of storage.
 *
 * @param {String} tokenName - Name of token to get.
 * @returns {String} Token that is stored under the tokenName, else empty string.
 */
export const _getToken = function* (tokenName) {
    const storage = yield call(_getStorage);
    const tokenValue = yield call(storage.getToken, tokenName);
    return tokenValue || '';
};

/**
 * Private method (exported for testing).
 * Save the specified token into storage.
 *
 * @param {String} tokenName - Name of token to set.
 * @param {String} tokenValue - Value of token to set.
 */
export const _setToken = function* (tokenName, tokenValue) {
    if (tokenName && typeof tokenName === 'string' && typeof tokenValue === 'string') {
        const storage = yield call(_getStorage);
        yield call(storage.setToken, tokenName, tokenValue);
    } else {
        throw new Error('Attempted to store an invalid token');
    }
};

/**
 * Private method (exported for testing).
 * Remove the specified token from storage.
 *
 * @param {String} tokenName - Name of token to remove.
 */
export const _removeToken = function* (tokenName) {
    if (tokenName && typeof tokenName === 'string') {
        const storage = yield call(_getStorage);
        yield call(storage.removeToken, tokenName);
    } else {
        throw new Error('Attempted to clear an invalid token');
    }
};

/**
 * Get the accessToken from storage.
 *
 * @param {Number} retries - Number of times to replay the request if it fails.
 * @returns {String} accessToken value.
 */
export const getAccessToken = function* (retries = 5) {
    for (let i = 0; i < retries; i += 1) {
        const accessToken = yield call(_getToken, 'access_token');
        if (accessToken) {
            return accessToken;
        }
        yield call(delay, 1000);
    }
    return '';
};

/**
 * Get the refreshToken from storage.
 *
 * @returns {String} refreshToken value.
 */
export const getRefreshToken = function* () {
    return yield call(_getToken, 'refresh_token');
};

/**
 * Save tokens to storage.
 *
 * @param {Object} apiResponse - Loosely coupled to the API response from "/api/account/token/anonymous/ctm".
 * @param {String} apiResponse.access_token - JSON Web Token to grant access to our API.
 * @param {String} apiResponse.refresh_token - JSON Web Token use to generate a "access_token" from our API.
 */
// eslint-disable-next-line camelcase
export const setTokens = function* ({ access_token, refresh_token }) {
    if (access_token) yield call(_setToken, 'access_token', access_token); // eslint-disable-line camelcase
    if (refresh_token) yield call(_setToken, 'refresh_token', refresh_token); // eslint-disable-line camelcase
};

/**
 * Clear tokens from storage.
 */
export const clearTokens = function* () {
    yield call(_removeToken, 'access_token');
    yield call(_removeToken, 'refresh_token');
};

/**
 * The Verticals that should return the CA session token
 * If Changing this list, please ensure that the CORs config has been updated for the specific vertical
 * Car and Home have already been done.
 */
const VERTICAL_ALLOW_LIST = [
    'car-insurance',
    'home-contents-insurance'
];

/**
 * Get the customer account session token
 * @param {Object} vertical - The current vertical
 * @returns {String} session token if available, empty string otherwise
 */
export const getCASessionToken = function* (vertical) {
    try {
        if (window && window.__getCASessionToken__ && VERTICAL_ALLOW_LIST.indexOf(vertical) > -1) {
            return yield window.__getCASessionToken__();
        }
        return '';
    } catch (e) {
        return '';
    }
};
